/**
 * Chunk Load Error Handler
 * 
 * Handles dynamic import failures that occur when:
 * 1. A new deployment changes chunk filenames (hashes)
 * 2. Users with cached old HTML try to load non-existent chunks
 * 3. Network failures prevent chunk loading
 * 
 * This utility detects chunk load errors and automatically reloads the page
 * to fetch the latest version of the application.
 */

// Maximum number of reload attempts to prevent infinite loops
const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_ATTEMPTS_KEY = 'chunk_reload_attempts';
const LAST_RELOAD_KEY = 'chunk_last_reload';

/**
 * Check if an error is a chunk load error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a chunk load error
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  
  // Common chunk load error patterns
  const chunkErrorPatterns = [
    'Failed to fetch dynamically imported module',
    'Failed to load module script',
    'error loading dynamically imported module',
    'ChunkLoadError',
    'Loading chunk',
    'Cannot find module',
    'Unexpected token', // Often happens when HTML is returned instead of JS
    'MIME type',
    'text/html', // Server returns HTML 404 page instead of JS
  ];
  
  return chunkErrorPatterns.some(pattern => 
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
};

/**
 * Check if the error is a module MIME type error (server returns HTML instead of JS)
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a MIME type error
 */
export const isMimeTypeError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString();
  
  return errorMessage.includes('MIME type') || 
         errorMessage.includes('text/html') ||
         errorMessage.includes('Expected a JavaScript module script');
};

/**
 * Get current reload attempts from sessionStorage
 * @returns {number} - Number of reload attempts
 */
const getReloadAttempts = () => {
  try {
    const attempts = sessionStorage.getItem(RELOAD_ATTEMPTS_KEY);
    return attempts ? parseInt(attempts, 10) : 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Increment reload attempts
 */
const incrementReloadAttempts = () => {
  try {
    const attempts = getReloadAttempts() + 1;
    sessionStorage.setItem(RELOAD_ATTEMPTS_KEY, attempts.toString());
    sessionStorage.setItem(LAST_RELOAD_KEY, Date.now().toString());
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Clear reload attempts (call this on successful load)
 */
export const clearReloadAttempts = () => {
  try {
    sessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);
    sessionStorage.removeItem(LAST_RELOAD_KEY);
  } catch (e) {
    // Ignore storage errors
  }
};

/**
 * Check if we should attempt a reload
 * @returns {boolean} - True if reload is allowed
 */
const shouldAttemptReload = () => {
  const attempts = getReloadAttempts();
  
  // Don't reload if we've exceeded max attempts
  if (attempts >= MAX_RELOAD_ATTEMPTS) {
    console.warn(`[ChunkLoadHandler] Max reload attempts (${MAX_RELOAD_ATTEMPTS}) reached. Not reloading.`);
    return false;
  }
  
  // Check if last reload was recent (prevent rapid reloads)
  try {
    const lastReload = sessionStorage.getItem(LAST_RELOAD_KEY);
    if (lastReload) {
      const timeSinceLastReload = Date.now() - parseInt(lastReload, 10);
      // Wait at least 3 seconds between reloads
      if (timeSinceLastReload < 3000) {
        return false;
      }
    }
  } catch (e) {
    // Ignore storage errors
  }
  
  return true;
};

/**
 * Handle chunk load error by reloading the page
 * @param {Error} error - The error that occurred
 * @param {Object} options - Options for handling
 * @param {boolean} options.forceReload - Force reload even if max attempts reached
 * @param {string} options.fallbackUrl - URL to redirect to if reload fails
 * @returns {boolean} - True if reload was triggered
 */
export const handleChunkLoadError = (error, options = {}) => {
  const { forceReload = false, fallbackUrl = '/dashboard' } = options;
  
  // Check if this is actually a chunk load error
  if (!isChunkLoadError(error)) {
    return false;
  }
  
  console.error('[ChunkLoadHandler] Chunk load error detected:', error.message);
  
  // Check if we should attempt reload
  if (!forceReload && !shouldAttemptReload()) {
    console.warn('[ChunkLoadHandler] Reload not allowed. Max attempts reached or too soon.');
    
    // Redirect to fallback URL as last resort
    if (fallbackUrl && window.location.pathname !== fallbackUrl) {
      window.location.href = fallbackUrl;
      return true;
    }
    
    return false;
  }
  
  // Increment attempts before reloading
  incrementReloadAttempts();
  
  // Add cache-busting parameter to force fresh load
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('_r', Date.now().toString());
  
  
  // Delay before reload to prevent rapid reload loops and allow user to see message
  setTimeout(() => {
    window.location.href = currentUrl.toString();
  }, 500);
  
  return true;
};

/**
 * Create a wrapped lazy import that handles chunk load errors
 * @param {Function} importFn - The dynamic import function
 * @param {Object} options - Options
 * @returns {Function} - Wrapped import function
 */
export const createSafeLazyImport = (importFn, options = {}) => {
  const { maxRetries = 1, fallbackComponent = null } = options;
  
  return async () => {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const module = await importFn();
        
        // Clear reload attempts on successful import
        clearReloadAttempts();
        
        return module;
      } catch (error) {
        lastError = error;
        
        // Check if it's a chunk load error
        if (isChunkLoadError(error)) {
          console.warn(`[ChunkLoadHandler] Lazy import failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error.message);
          
          // On first failure, try to reload the page
          if (attempt === 0 && handleChunkLoadError(error)) {
            // Return a promise that never resolves (page is reloading)
            return new Promise(() => {});
          }
          
          // Wait before retry
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          }
        } else {
          // Not a chunk error, throw immediately
          throw error;
        }
      }
    }
    
    // All retries failed
    throw lastError;
  };
};

/**
 * Setup global error handler for unhandled chunk load errors
 */
export const setupGlobalChunkErrorHandler = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    
    if (isChunkLoadError(error)) {
      console.error('[ChunkLoadHandler] Unhandled chunk load error:', error);
      
      if (handleChunkLoadError(error)) {
        // Prevent default error handling
        event.preventDefault();
      }
    }
  });
  
  // Handle global errors
  window.addEventListener('error', (event) => {
    const error = event.error;
    
    if (isChunkLoadError(error)) {
      console.error('[ChunkLoadHandler] Global chunk load error:', error);
      
      if (handleChunkLoadError(error)) {
        // Prevent default error handling
        event.preventDefault();
      }
    }
  });
  
};

/**
 * Get a user-friendly error message for chunk load errors
 * @param {Error} error - The error
 * @returns {string} - User-friendly message
 */
export const getChunkErrorMessage = (error) => {
  if (isMimeTypeError(error)) {
    return 'The application has been updated. Please refresh the page to load the latest version.';
  }
  
  if (isChunkLoadError(error)) {
    return 'Failed to load application components. Please check your connection and refresh the page.';
  }
  
  return 'An unexpected error occurred. Please refresh the page and try again.';
};

export default {
  isChunkLoadError,
  isMimeTypeError,
  handleChunkLoadError,
  createSafeLazyImport,
  setupGlobalChunkErrorHandler,
  clearReloadAttempts,
  getChunkErrorMessage,
};
